/**
 * Click-capture hook for the scan-pattern wizard. Routes map clicks into
 * the store's scan-draw state while `scanDrawMode` is non-null:
 *
 *   - 'polygon' mode : every click appends a vertex. Enter (≥3 vertices)
 *                      finalizes — calls generateSerpentine + bulk-add
 *                      into the selected mission. Esc cancels.
 *   - 'line' mode    : the FIRST click sets a start vertex; the SECOND
 *                      click finalizes — calls generateLine + bulk-add.
 *                      Esc cancels.
 *
 * The hook gates internally on scanDrawMode and selectedMissionId, so it
 * can be mounted in a panel that only conditionally renders; the effect
 * simply early-exits when there's nothing to draw.
 */

import { useEffect } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { generateSerpentine, generateLine } from '../../utils/scanPatterns';

export function useDemoScanDraw(): void {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const scanDrawMode = useDockDemoStore((s) => s.scanDrawMode);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const appendScanDrawVertex = useDockDemoStore((s) => s.appendScanDrawVertex);
  const clearScanDraft = useDockDemoStore((s) => s.clearScanDraft);
  const addWaypointsBulk = useDockDemoStore((s) => s.addWaypointsBulk);

  useEffect(() => {
    if (!map || !isMapReady || !scanDrawMode || !selectedMissionId) return;

    const finalizePolygon = () => {
      const cur = useDockDemoStore.getState();
      const verts = cur.scanDrawVertices;
      if (verts.length < 3) return;
      const sweep = generateSerpentine(verts, { lineSpacingM: cur.scanLineSpacingM });
      if (sweep.length === 0) {
        // Polygon was too small to fit a single sweep line after the
        // half-spacing inset — just bail and clear the draft so the user
        // can redraw a bigger one (or pick a finer spacing).
        clearScanDraft();
        return;
      }
      addWaypointsBulk(
        selectedMissionId,
        sweep.map(([lng, lat]) => ({ lng, lat })),
      );
      clearScanDraft();
    };

    const finalizeLine = () => {
      const cur = useDockDemoStore.getState();
      const verts = cur.scanDrawVertices;
      if (verts.length !== 2) return;
      const pts = generateLine(verts[0], verts[1], { stepM: cur.scanLineStepM });
      if (pts.length === 0) {
        clearScanDraft();
        return;
      }
      addWaypointsBulk(
        selectedMissionId,
        pts.map(([lng, lat]) => ({ lng, lat })),
      );
      clearScanDraft();
    };

    const onClick = (e: MapMouseEvent) => {
      const cur = useDockDemoStore.getState();
      // Re-check mode inside the handler — scanDrawMode could have flipped
      // off (Esc or panel cancel) between effect-bind and click-fire.
      if (!cur.scanDrawMode) return;
      appendScanDrawVertex(e.lngLat.lng, e.lngLat.lat);
      // Line mode auto-finalizes on the 2nd click. We read the verts AFTER
      // appending so the freshly-added vertex is included.
      if (cur.scanDrawMode === 'line') {
        const next = useDockDemoStore.getState().scanDrawVertices;
        if (next.length >= 2) finalizeLine();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const cur = useDockDemoStore.getState();
      if (e.key === 'Enter') {
        e.preventDefault();
        if (cur.scanDrawMode === 'polygon') finalizePolygon();
        else if (cur.scanDrawMode === 'line') finalizeLine();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearScanDraft();
      }
    };

    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';
    map.on('click', onClick);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      map.off('click', onClick);
      window.removeEventListener('keydown', onKeyDown);
      canvas.style.cursor = previousCursor;
    };
  }, [
    map,
    isMapReady,
    scanDrawMode,
    selectedMissionId,
    appendScanDrawVertex,
    clearScanDraft,
    addWaypointsBulk,
  ]);
}
